import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import { DateHelper } from "src/gyzmo-commons/helpers/date.helper";
import { UserDto } from "../core/dto/user.dto";
import { UserService } from "../core/services/user.service";
import { DeviceHelper, PlatformName } from "../gyzmo-commons/helpers/device.helper";
import { LoadingHelper } from "../gyzmo-commons/helpers/loading.helper";
import { NavControllerExtended } from "../gyzmo-commons/helpers/navControllerExtended";
import { NetworkHelper } from "../gyzmo-commons/helpers/network.helper";
import { DateProvider } from "../gyzmo-commons/interfaces/dateProvider";
import { LoggerService } from "../gyzmo-commons/services/logs/logger.service";
import { APP_OPTIONS } from "./app.constants";

export abstract class ListBasePage {
    DateHelper = DateHelper;

    today: DateTime;
    dayIndex: string = "0";
    page: number = 0;

    daysIndexes: number[];
    operationsCount: number[][];
    daysNames: string[];
    daysDateString: string[];
    daysDates: DateTime[];

    isConnected = false;
    user: UserDto;

    protected constructor(protected navCtrl: NavControllerExtended,
                          protected logger: LoggerService,
                          protected deviceHelper: DeviceHelper,
                          protected dateProvider: DateProvider,
                          protected networkHelper: NetworkHelper,
                          protected userService: UserService,
                          protected alertCtrl: AlertController,
                          protected translateService: TranslateService,
                          protected loadingHelper: LoadingHelper) {
        this.today = dateProvider.now();
        let daysCountToDisplay = this.getDaysCountToDisplay();

        this.daysIndexes = Array(daysCountToDisplay).fill(0).map((x, i) => i);
        this.daysNames = Array(daysCountToDisplay).fill("");
        this.daysDateString = Array(daysCountToDisplay).fill("");
        this.daysDates = Array(daysCountToDisplay).fill(DateTime.now());

        this.operationsCount = [];
        this.isConnected = networkHelper.isConnected();
    }

    async ionViewDidEnter() {
        this.user = await this.userService.getCurrentUserWithThirdPartyOnly();
    }

    public getOperationsCount(dayIndex: number): number[] {
        if (this.operationsCount.length > dayIndex) {
            return this.operationsCount[dayIndex];
        }

        return [0, 0, 0];
    }

    public disconnect() {
        this.alertCtrl.create({
            header: this.translateService.instant("IMCP_Logout"),
            message: this.translateService.instant("IMCP_Would_you_like_to_log_out?"),
            buttons: [{
                text: this.translateService.instant("IMCP_Cancel"),
                role: "cancel",
                handler: () => {
                    this.logger.info(this.constructor.name, "Disconnection prevented!");
                },
            },
                {
                    text: this.translateService.instant("IMCP_Confirm"),
                    handler: () => {
                        this.userService.disconnect(true)
                            .then(async value => {
                                await this.navCtrl.navigateRoot("connection");
                            });
                    },
                }],
        }).then(alert => alert.present());
    }

    public async previousPage() {
        this.page--;

        let newDayIndex = "" + (this.getDaysCountToDisplay() - 1);
        if (this.dayIndex == newDayIndex) {
            await this.refreshDayInspection();
        } else {
            this.dayIndex = newDayIndex;
        }

        await this.refresh();
    }

    public async nextPage() {
        this.page++;

        let newDayIndex = "0";
        if (this.dayIndex == newDayIndex) {
            await this.refreshDayInspection();
        } else {
            this.dayIndex = newDayIndex;
        }

        await this.refresh();
    }

    public async goToToday() {
        this.page = 0;

        let newDayIndex = "0";
        if (this.dayIndex == newDayIndex) {
            await this.refreshDayInspection();
        } else {
            this.dayIndex = newDayIndex;
        }

        await this.refresh();
    }

    public refresh(event?) {
        let promises = [];

        promises.push(this.refreshDayInspection());
        promises.push(this.onPageChanged(this.page));

        return Promise.all(promises)
            .then(value => {
                if (event) {
                    event.target.complete();
                }
            });
    }

    public async onDayTabChanged($event: any) {
        this.dayIndex = $event.detail.value;

        await this.refreshDayInspection();
    }

    public getDay(dayIndex: number, page: number): DateTime {
        const dayIndexFromToday = dayIndex + page * this.getDaysCountToDisplay();

        return this.dateProvider.now().plus({ days: dayIndexFromToday });
    }

    protected fillDaysNamesAndDates(page: number) {
        let daysDates = [];
        let daysNames = [];
        let daysDateString = [];

        for (let i = 0; i < this.daysIndexes.length; i++) {
            const dayIndexFromToday = i + page * this.getDaysCountToDisplay();

            let dateTime = this.dateProvider.now().plus({ days: dayIndexFromToday });
            daysDates.push(dateTime);
            daysNames.push(dateTime.toFormat("ccc"));
            daysDateString.push(dateTime.toFormat("dd MMM"));
        }

        this.daysDates = daysDates;
        this.daysNames = daysNames;
        this.daysDateString = daysDateString;
    }

    protected getDaysCountToDisplay(): number {
        let count = 0;

        if (this.deviceHelper.getPlatform() == PlatformName.IOS || APP_OPTIONS.mode == "ios") {
            count = Math.floor((window.innerWidth - 8 - 100) / 70);
        } else {
            count = Math.floor((window.innerWidth - 100) / 75);
        }

        return Math.max(3, count);
    }

    public abstract onPageChanged(page: number): Promise<void>;

    public abstract refreshDayInspection();
}
